import {Injectable} from '@angular/core';
import {BaseService} from '@app/core/services/base.service';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {HttpService} from '@app/core/services/http.service';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {Observable} from 'rxjs';
import {Shift} from '@app/core/models/shift.model';
import {Department} from '@app/core/models/department.model';
import {map} from 'rxjs/operators';
import {ShiftService} from '@app/modules/shared/services/shift.service';
import {IUserTotalCalcWithDepartment} from '@app/core/interfaces/salary';
import {IPunchSettings, IWeather} from '@app/core/store/state/dashboard.state';

export interface IPunchStatusReponse {
	shift: Shift;
	departments: Department[];
	punched_in: boolean;
}

export interface IPunchStartParams {
	group_id: number;
	department_id: number;
	lat?: string;
	lng?: string;
}

export interface IPunchEndParams {
	punch_note: string;
	lat?: string;
	lng?: string;
}

@Injectable({
	providedIn: 'root',
})
export class DashboardService extends BaseService {

	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		public store: Store<IAppState>,
		private shiftService: ShiftService,
	) {
		super(authService, interService, httpService, store);
	}

	public getNextShifts(pageSize: number): Observable<Shift[]> {
		return this.httpService.getJson<Shift[]>('shifts/nextShifts', {limit: pageSize}).pipe(map((res: Shift[]) => res.map(shift => this.shiftService.fresh(shift))));
	}

	public getActionsCount(): Observable<any> {
		return this.httpService.getJson<any>('actions/count');
	}

	public getDashboardUserSalaryCalculation(userId: number, year: number, month: number): Observable<IUserTotalCalcWithDepartment> {
		return this.httpService.getJson<IUserTotalCalcWithDepartment>('salary/users/' + userId, {year, month});
	}

	public getPunchStatus(): Observable<IPunchStatusReponse> {
		return this.httpService.getJson<IPunchStatusReponse>('punchStatus').pipe(map((res: any) => {
			res.departments = res.departments.map(department => new Department(this.tenantTZ, this.interService).deserialize(department));
			if (res.shift) {
				res.shift = new Shift(this.tenantTZ, this.interService).deserialize(res.shift);
			}

			return res;
		}));

	}

	public getPunchSettings(): Observable<IPunchSettings> {
		return this.httpService.getJson<IPunchSettings>('punchSettings');
	}

	public punchStart(data: IPunchStartParams): Observable<Shift> {
		return this.httpService.postJson<Shift>('punchStart', data).pipe(map((res: any) => {
			return new Shift(this.tenantTZ, this.interService).deserialize(res.shift);
		}));

	}

	public punchEnd(data: IPunchEndParams): Observable<Shift> {
		return this.httpService.postJson<Shift>('punchEnd', data);
	}

	public getWeatherData(postal_code: string, country_code: string): Observable<IWeather[]> {
		return this.httpService.getJson<IWeather>('weather', {sort: '-created_at', page_size: '1', 'filter[postal_code]': postal_code, 'filter[country_code]': country_code})
			.pipe(map((res: any) => res.data));
	}
}
